import { useLayoutEffect, useRef } from "react";

import classnames from "classnames";
import { noop, isNotEmpty, isPresent } from "neetocist";
import { Typography } from "neetoui";
import PropTypes from "prop-types";

import Breadcrumbs from "components/Breadcrumbs";
import HelpPopover from "components/HelpPopover";
import MoreDropdown from "components/MoreDropdown";
import Search from "components/Search";

import { DROPDOWN_BUTTON_SIZES, SIZES } from "./constants";
import "./header.scss";

const Header = ({
  title,
  titleHelpPopoverProps = {},
  searchProps,
  className = "",
  actionBlock,
  breadcrumbs = [],
  renderDropdown = noop,
  size = "small",
  "data-testid": dataTestId = "neeto-molecules-header",
}) => {
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      if (!headerRef.current) return;
      const height = headerRef.current.offsetHeight;
      document.body.style.setProperty(
        "--neeto-molecules-effective-header-height",
        `${height}px`
      );
    };

    updateHeaderHeight();

    window.addEventListener("resize", updateHeaderHeight);

    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  return (
    <div
      data-testid={dataTestId}
      ref={headerRef}
      className={classnames([
        "neeto-molecules-header",
        {
          "neeto-molecules-header--has-breadcrumbs": isNotEmpty(breadcrumbs),
          "neeto-molecules-header--small": size === SIZES.small,
          "neeto-molecules-header--nano": size === SIZES.nano,
        },
        className,
      ])}
    >
      <div className="neeto-molecules-header__left">
        <div className="neeto-molecules-header__left-data-wrap">
          {isNotEmpty(breadcrumbs) && <Breadcrumbs {...{ breadcrumbs }} />}
          <div className="neeto-molecules-header__page-title">
            <Typography
              data-testid="main-header"
              lineHeight="tight"
              style={size === SIZES.nano ? "h3" : "h1"}
              weight="semibold"
            >
              {title}
              {isPresent(titleHelpPopoverProps) && (
                <HelpPopover {...titleHelpPopoverProps} />
              )}
              {renderDropdown(
                ({ dropdownButtonProps = {}, ...dropdownProps }) => (
                  <MoreDropdown
                    dropdownButtonProps={{
                      size: DROPDOWN_BUTTON_SIZES[size],
                      ...dropdownButtonProps,
                    }}
                    {...dropdownProps}
                  />
                )
              )}
            </Typography>
          </div>
        </div>
      </div>
      <div className="neeto-molecules-header__right">
        {searchProps && (
          <Search
            enableUrlSync
            className={classnames([
              "neeto-molecules-header__search-input",
              searchProps.className,
            ])}
            {...searchProps}
          />
        )}
        {actionBlock && (
          <div className="neeto-molecules-header__action-block">
            {actionBlock}
          </div>
        )}
      </div>
    </div>
  );
};

Header.propTypes = {
  /**
   * To set a title in the Header section.
   */
  title: PropTypes.node,
  /**
   * To set props for the HelpPopover component in the title.
   */
  titleHelpPopoverProps: PropTypes.object,
  /**
   * To specify additional class names.
   */
  className: PropTypes.string,
  /**
   * To specify the content to be rendered in the right side of the Header section.
   */

  actionBlock: PropTypes.node,
  /**
   * To add **Search** molecule to the subheader section.
   */
  searchProps: PropTypes.object,
  /**
   * To show breadcrumbs in the Header section. `text` accepts a text string to be shown as the breadcrumb and `link` accepts relative URL path for the breadcrumb.
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  /**
   * A function to render the Dropdown next to the title. It provides the MoreDropdown component as argument.
   */
  renderDropdown: PropTypes.func,
  /**
   * To specify size of the Header.
   * `small` - Default size.
   *
   * `nano` - To be used in inner pages with menubar.
   */
  size: PropTypes.oneOf(["nano", "small"]),
};

export default Header;
